import { useNavigate } from "react-router-dom";
import { LockKeyhole } from "lucide-react";
import styles from "@/components/workspace/DecisionWorkspaceVisual.module.css";
import { Button } from "@/components/ax/Button";
import { DecisionCard } from "@/components/ax/DecisionCard";
import { PrimaryBar } from "@/components/ax/PrimaryBar";
import { BriefPanel } from "@/components/workspace/BriefPanel";
import { Masthead } from "@/components/workspace/Masthead";
import { FactsSection, OptionsSection } from "@/components/workspace/Sections";
import { RelatedChaptersSection } from "@/components/workspace/RelatedChaptersSection";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useI18n } from "@/i18n";
import { withRefs } from "@/lib/flags";
import { canResumeIntake, intakeUrl } from "@/lib/intakeResume";

const fmt = (lang, iso) =>
  new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "id-ID", { day: "numeric", month: "short", year: "numeric" }).format(new Date(iso));

const Skeleton = () => (
  <div aria-busy="true" data-testid="workspace-skeleton" className={`${styles.skeleton} flex flex-col gap-3`}>
    <div className="h-8 w-2/3 rounded-[8px] bg-ax-border" />
    <div className="h-4 w-full rounded-[8px] bg-ax-border" />
    <div className="h-24 rounded-[12px] border border-ax-border bg-ax-surface" />
  </div>
);

export default function Workspace() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const w = useWorkspace();

  if (w.state === "loading") return <Skeleton />;
  if (w.state !== "ready") {
    return (
      <div role="alert" data-testid="workspace-error" className={styles.error}>
        <p className="m-0 text-base text-ax-text-2">{t(w.state === "missing" ? "ws.notFound" : "err.load")}</p>
        {w.state === "error" && (
          <Button onClick={w.load} data-testid="workspace-retry-button" className="mt-4">
            {t("retry")}
          </Button>
        )}
      </div>
    );
  }

  const d = w.decision;
  const items = withRefs(d.items);
  const chosen = d.options.find((o) => o.id === d.selectedOptionId);
  const meta = [
    d.nextAction && [t("ws.nextAction"), d.nextAction],
    d.reviewAt && [t("ws.review"), fmt(lang, d.reviewAt)],
    d.committedAt && [t("ws.commitDate"), fmt(lang, d.committedAt)],
  ].filter(Boolean);

  return (
    <div data-testid="workspace-page" className={styles.page}>
      <Masthead w={w} />

      <FactsSection w={w} items={items} />
      <OptionsSection w={w} />
      {canResumeIntake(d) && <Button
        onClick={() => navigate(intakeUrl(d.id))}
        data-testid="resume-intake-button"
        className="mt-4"
      >{t("intake.resume")}</Button>}
      <BriefPanel w={w} items={items} />

      {w.committed && (
        <div className={styles.decision}>
          <DecisionCard
            optionName={d.status === "deferred" ? t("ws.deferred") : chosen?.name ?? ""}
            rationale={d.rationale}
            meta={meta}
          />
        </div>
      )}

      <RelatedChaptersSection chapters={w.relatedChapters} locked={w.relatedLocked} />

      {!w.committed && (
        <section className={styles.commitPanel} aria-labelledby="workspace-record-title" data-testid="workspace-record-panel">
          <p className={styles.decisionLabel}>{t("layer.decision")}</p>
          <h2 id="workspace-record-title" className={styles.decisionTitle}>{t("workspace.recordTitle")}</h2>
          <p className={styles.decisionHint}>{t("workspace.recordHint")}</p>
          <PrimaryBar>
            <Button
              variant="primary"
              disabled={d.options.length < 2 || !!w.busy}
              onClick={() => navigate(`/keputusan/${d.id}/commit`)}
              data-testid="commit-button"
              className="w-full md:w-auto md:min-w-[200px]"
            >
              <LockKeyhole size={18} strokeWidth={1.5} aria-hidden="true" />
              {t("ws.commit")}
            </Button>
          </PrimaryBar>
        </section>
      )}
    </div>
  );
}
